import React, { useState, useRef, useEffect, useCallback } from "react";
import "./styles/ChatWidget.scss";

// Quick-start prompt suggestions shown before the first message
const SUGGESTIONS = [
  "Tell me about your background",
  "What projects have you built?",
  "What are your technical skills?",
  "What's your work experience?",
  "How can I get in touch?",
];

// Detect if we're running locally (dev) or on Vercel
const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:3001/api/chat"
    : "/api/chat";

function MarkdownText({ text }) {
  // Very lightweight inline markdown renderer: bold, code, links
  const lines = text.split("\n");
  return (
    <div className="md-content">
      {lines.map((line, i) => {
        // Heading
        if (line.startsWith("### "))
          return <h4 key={i} className="md-h4">{line.replace("### ", "")}</h4>;
        if (line.startsWith("## "))
          return <h3 key={i} className="md-h3">{line.replace("## ", "")}</h3>;
        if (line.startsWith("# "))
          return <h2 key={i} className="md-h2">{line.replace("# ", "")}</h2>;
        // Bullet
        if (line.startsWith("- ") || line.startsWith("* ")) {
          const content = line.replace(/^[-*]\s/, "");
          return (
            <li key={i} className="md-li">
              {renderInline(content)}
            </li>
          );
        }
        // Blank line
        if (line.trim() === "") return <br key={i} />;
        // Normal paragraph
        return <p key={i} className="md-p">{renderInline(line)}</p>;
      })}
    </div>
  );
}

function renderInline(text) {
  // Parse **bold**, `code`, and [link](url)
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`(.+?)`/);
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

    const candidates = [boldMatch, codeMatch, linkMatch]
      .filter(Boolean)
      .sort((a, b) => a.index - b.index);

    if (candidates.length === 0) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    const first = candidates[0];
    if (first.index > 0) {
      parts.push(<span key={key++}>{remaining.slice(0, first.index)}</span>);
    }

    if (first === boldMatch) {
      parts.push(<strong key={key++}>{first[1]}</strong>);
    } else if (first === codeMatch) {
      parts.push(<code key={key++} className="md-code">{first[1]}</code>);
    } else if (first === linkMatch) {
      parts.push(
        <a key={key++} href={first[2]} target="_blank" rel="noopener noreferrer" className="md-link">
          {first[1]}
        </a>
      );
    }

    remaining = remaining.slice(first.index + first[0].length);
  }

  return parts;
}

// Animated typing dots
function TypingIndicator() {
  return (
    <div className="chat-message bot typing-indicator-wrapper">
      <div className="chat-avatar bot-avatar">P</div>
      <div className="message-bubble bot-bubble">
        <div className="typing-dots">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showNudge, setShowNudge] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Auto-scroll to newest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  // Show nudge tooltip after a few seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) setShowNudge(true);
    }, 3500); // 3.5s delay
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Clean up any in-flight request on unmount
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading || cooldown > 0) return;

      const userMsg = { role: "user", content: trimmed, id: Date.now() };
      setMessages((prev) => [...prev, userMsg]);
      setInputValue("");
      setIsLoading(true);

      // Build history for API (exclude ids)
      const history = messages.map(({ role, content }) => ({ role, content }));

      abortRef.current = new AbortController();

      try {
        const res = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: trimmed, history }),
          signal: abortRef.current.signal,
        });

        const data = await res.json();

        if (!res.ok) throw new Error(data.error || "Server error");

        const botMsg = {
          role: "assistant",
          content: data.reply,
          id: Date.now() + 1,
        };
        setMessages((prev) => [...prev, botMsg]);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Chat error:", err);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Sorry, I'm having trouble connecting right now. Please try again in a moment!",
            id: Date.now() + 1,
            isError: true,
          },
        ]);
      } finally {
        setIsLoading(false);
        // Start 3 second frontend cooldown
        setCooldown(3);
        const interval = setInterval(() => {
          setCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    },
    [isLoading, messages, cooldown]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleSuggestion = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) setShowNudge(false);
  };

  const handleClear = () => {
    setMessages([]);
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="chat-widget-root">
      {/* ── Chat Panel ── */}
      <div className={`chat-panel ${isOpen ? "chat-panel--open" : ""}`} role="dialog" aria-label="Chat with Pratyush's Digital Twin">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="chat-header-avatar">P</div>
            <div>
              <span className="chat-header-name">Pratyush's Digital Twin</span>
              <span className="chat-header-status">
                <span className="status-dot" />
                Online
              </span>
            </div>
          </div>
          <div className="chat-header-actions">
            {messages.length > 0 && (
              <button
                className="chat-icon-btn"
                onClick={handleClear}
                title="Clear chat"
                aria-label="Clear chat history"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
              </button>
            )}
            <button
              className="chat-icon-btn chat-close-btn"
              onClick={handleToggle}
              title="Close chat"
              aria-label="Close chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div className="chat-messages" aria-live="polite">
          {isEmpty ? (
            <div className="chat-welcome">
              <div className="welcome-avatar">P</div>
              <h4 className="welcome-title">Hey there! 👋</h4>
              <p className="welcome-subtitle">
                I'm Pratyush's digital twin. Ask me anything about his skills,
                projects, or experience.
              </p>
              <div className="suggestions-grid">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    className="suggestion-chip"
                    onClick={() => handleSuggestion(s)}
                    disabled={isLoading}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`chat-message ${msg.role === "user" ? "user" : "bot"} ${msg.isError ? "error" : ""}`}
                >
                  {msg.role === "assistant" && (
                    <div className="chat-avatar bot-avatar">P</div>
                  )}
                  <div className={`message-bubble ${msg.role === "user" ? "user-bubble" : "bot-bubble"}`}>
                    {msg.role === "assistant" ? (
                      <MarkdownText text={msg.content} />
                    ) : (
                      <p>{msg.content}</p>
                    )}
                  </div>
                  {msg.role === "user" && (
                    <div className="chat-avatar user-avatar">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
              {isLoading && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input row */}
        <div className="chat-input-row">
          <textarea
            ref={inputRef}
            className="chat-textarea"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything about Pratyush…"
            rows={1}
            disabled={isLoading || cooldown > 0}
            aria-label="Type your message"
          />
          <button
            className="chat-send-btn"
            onClick={() => sendMessage(inputValue)}
            disabled={isLoading || cooldown > 0 || !inputValue.trim()}
            aria-label="Send message"
            title={cooldown > 0 ? `Wait ${cooldown}s` : "Send (Enter)"}
          >
            {isLoading ? (
              <svg className="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* ── FAB Toggle Button ── */}
      <button
        className={`chat-fab ${isOpen ? "chat-fab--open" : ""}`}
        onClick={handleToggle}
        aria-label={isOpen ? "Close chat" : "Chat with Pratyush's AI Twin"}
        title="Chat with Pratyush's Digital Twin"
      >
        <span className="chat-fab-icon chat-fab-icon--chat">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
        </span>
        <span className="chat-fab-icon chat-fab-icon--close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </span>
        {!isOpen && (
          <span className="chat-fab-pulse" />
        )}
      </button>

      {/* ── Nudge Tooltip ── */}
      {!isOpen && (
        <div 
          className={`chat-nudge ${showNudge ? "chat-nudge--visible" : ""}`}
          onClick={handleToggle}
        >
          Chat with my Digital Twin! <span className="wave">✨</span>
        </div>
      )}
    </div>
  );
}
